/**
 * Downloading a workbook the Copilot built.
 *
 * Separate from `frameExport` because the two are different things: a frame
 * export rebuilds a file from a table still living in the session's DuckDB and
 * can 410 once that table is gone, while a workbook's bytes are stored — the
 * file is handed over as saved, however old the thread is.
 *
 * The request carries the viewer's own token, so a download is authorised as
 * them, not as the agent that built it.
 */

import { buildApiUrl, tenantApiPrefix } from '../config';
import type { XlsxPreviewSheet } from '../ui-kit';
import { getTenantIdFromStorage } from './onboardingLocalStorage';

export class WorkbookDownloadError extends Error {
  status: number;

  constructor(message: string, status: number) {
    super(message);
    this.name = 'WorkbookDownloadError';
    this.status = status;
  }
}

function filenameFrom(disposition: string | null, fallback: string): string {
  if (!disposition) return fallback;
  const match = /filename\*?=(?:UTF-8'')?"?([^";]+)"?/i.exec(disposition);
  return match?.[1] ? decodeURIComponent(match[1]) : fallback;
}

/** A workbook read back as a grid, for looking at without downloading. */
export interface WorkbookPreview {
  workbookId: string;
  title: string;
  sheets: XlsxPreviewSheet[];
  /** Sheets the server left out, for a workbook too big to send whole. */
  sheetsOmitted: number;
}

/**
 * Read the workbook's sheets back.
 *
 * The rows come from the stored file itself, not from the recipe it was built
 * from, so what someone previews is the file they would get.
 */
export async function fetchWorkbookPreview(workbookId: string): Promise<WorkbookPreview> {
  const tenantId = getTenantIdFromStorage();
  const token = localStorage.getItem('klaris_jwt');
  if (!tenantId || !token) throw new WorkbookDownloadError('You are signed out. Sign in again.', 401);

  const response = await fetch(
    buildApiUrl(`${tenantApiPrefix(tenantId)}/workbooks/${encodeURIComponent(workbookId)}/preview`),
    { headers: { Authorization: `Bearer ${token}` } },
  );
  if (!response.ok) {
    throw new WorkbookDownloadError(
      response.status === 404
        ? 'That workbook is no longer available. Ask me to build it again.'
        : 'This workbook could not be previewed. Download it to open it in Excel.',
      response.status,
    );
  }

  const raw = await response.json();
  return {
    workbookId: String(raw?.workbook_id || workbookId),
    title: String(raw?.title || 'Workbook'),
    sheets: (raw?.sheets || []) as XlsxPreviewSheet[],
    sheetsOmitted: Number(raw?.sheets_omitted ?? 0),
  };
}

/** Fetch the workbook and save it, returning the name it was saved under. */
export async function downloadWorkbook(
  workbookId: string,
  fallbackName = 'workbook.xlsx',
): Promise<{ filename: string }> {
  const tenantId = getTenantIdFromStorage();
  const token = localStorage.getItem('klaris_jwt');
  if (!tenantId || !token) throw new WorkbookDownloadError('You are signed out. Sign in again to download.', 401);

  const response = await fetch(
    buildApiUrl(`${tenantApiPrefix(tenantId)}/workbooks/${encodeURIComponent(workbookId)}/download`),
    { headers: { Authorization: `Bearer ${token}` } },
  );
  if (!response.ok) {
    const detail =
      response.status === 404
        ? 'That workbook is no longer available. Ask me to build it again.'
        : 'The workbook could not be downloaded.';
    throw new WorkbookDownloadError(detail, response.status);
  }

  const filename = filenameFrom(response.headers.get('Content-Disposition'), fallbackName);
  const blob = await response.blob();
  const url = window.URL.createObjectURL(blob);
  try {
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    link.remove();
  } finally {
    window.URL.revokeObjectURL(url);
  }
  return { filename };
}
